import { useEffect, useRef, useState } from 'react';
import { Compass, MousePointerClick, Sparkles, ArrowRight } from 'lucide-react';
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { 
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";

const coreValues = [
  {
    title: "Free Discovery Phase",
    icon: <Compass className="h-8 w-8 text-primary" />,
    content: "Over two weeks we dig into your business, your users, and the problem worth solving, then define what a first version should include. You walk away with a clear scope and a fixed-price quote, whether or not you decide to continue.",
    footer: '"Clarity first. Everything else follows."',
    image: "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?auto=format&fit=crop&w=1200&q=80"
  },
  {
    title: "Free Working Prototype",
    icon: <MousePointerClick className="h-8 w-8 text-primary" />,
    content: "At the end of discovery we hand you a clickable prototype of your core idea so you can see it, test it, and show it to your team before spending a dollar. Not a slide deck. Something you can actually click through.",
    footer: '"See it working before you decide anything."',
    image: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1200&q=80"
  }
];

const CoreValues = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const carouselApiRef = useRef<any>(null);
  const [autoplayInterval, setAutoplayInterval] = useState<NodeJS.Timeout | null>(null);

  const handlePrincipleClick = (index: number) => {
    setActiveIndex(index);
    carouselApiRef.current?.scrollTo(index);
    
    // Reset autoplay timer when user manually selects a principle
    if (autoplayInterval) {
      clearInterval(autoplayInterval);
      startAutoplay();
    }
  };

  const startAutoplay = () => {
    // Clear any existing interval
    if (autoplayInterval) {
      clearInterval(autoplayInterval);
    }
    
    // Create new interval
    const interval = setInterval(() => {
      setActiveIndex((prevIndex) => {
        const newIndex = (prevIndex + 1) % coreValues.length;
        carouselApiRef.current?.scrollTo(newIndex);
        return newIndex;
      });
    }, 5000); // Change image every 5 seconds
    
    setAutoplayInterval(interval);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true);
          setHasAnimated(true);
          observer.disconnect();
          
          // Start autoplay when section becomes visible
          startAutoplay();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
      if (autoplayInterval) {
        clearInterval(autoplayInterval);
      }
    };
  }, [hasAnimated]);

  return (
    <section
      id="values"
      ref={sectionRef}
      className="py-12 pb-6 px-6 md:px-10 lg:px-20 relative overflow-hidden bg-background"
    >
      {/* Enhanced dynamic gradient background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 opacity-40 bg-gradient-to-br from-primary/20 via-primary/5 to-transparent" 
          style={{
            background: "radial-gradient(circle at 30% 40%, rgba(155, 135, 245, 0.15), transparent 70%), radial-gradient(circle at 70% 60%, rgba(14, 165, 233, 0.1), transparent 70%)"
          }}
        />
      </div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className={cn(
          "text-center mb-12 transition-all duration-1000",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        )}>
          <span className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary rounded-lg text-sm font-medium mb-4">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-primary/60 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
            </span>
            LIMITED OFFER
          </span>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Free Discovery &amp; Prototype. No Commitment.</h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            For a limited number of new projects, we run the full discovery phase and build a working prototype at no cost. See what we can do before you spend anything.
          </p>
        </div>
        
        {/* Adjusted grid layout with smaller carousel */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Left side principles */}
          <div className="lg:col-span-5 space-y-1">
            {coreValues.map((value, index) => (
              <div 
                key={index}
                className={cn(
                  "p-3 transition-all duration-300 cursor-pointer relative rounded-lg",
                  index === activeIndex ? "bg-primary/10 shadow-md" : "hover:bg-primary/5",
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                )}
                style={{ transitionDelay: `${index * 150}ms` }}
                onClick={() => handlePrincipleClick(index)}
              >
                {/* Glowing left accent indicator */}
                <div 
                  className={cn(
                    "absolute left-0 top-0 w-1 h-full rounded-l-lg transition-all duration-300",
                    index === activeIndex ? "bg-primary shadow-[0_0_15px_rgba(var(--primary)/0.6)]" : "bg-transparent"
                  )}
                />
                
                <div className="flex items-center gap-2 mb-1">
                  <div className={cn(
                    "transition-all duration-300 transform",
                    index === activeIndex ? "text-primary scale-110" : "text-primary/70"
                  )}>
                    {value.icon}
                    {index === activeIndex && (
                      <Sparkles className="h-3 w-3 text-primary absolute -top-1 -right-1 animate-pulse" />
                    )}
                  </div>
                  <h3 className={cn(
                    "text-lg font-semibold transition-colors duration-300",
                    index === activeIndex ? "text-foreground" : "text-foreground/80"
                  )}>
                    {value.title}
                  </h3>
                </div>
                
                <p className="text-base text-muted-foreground mb-2">{value.content}</p>
                
                {/* Enhanced divider with glow effect */}
                <div className="relative my-1 h-px w-full overflow-hidden">
                  <div className={cn(
                    "absolute inset-0 transition-all duration-300",
                    index === activeIndex 
                      ? "bg-gradient-to-r from-transparent via-primary/80 to-transparent opacity-100 shadow-[0_0_10px_rgba(var(--primary)/0.7)]" 
                      : "bg-gradient-to-r from-transparent via-primary/30 to-transparent opacity-30"
                  )}></div>
                </div>
                
                <div className={cn(
                  "text-xs italic transition-colors duration-300",
                  index === activeIndex ? "text-primary/90" : "text-muted-foreground"
                )}>
                  {value.footer}
                </div>
              </div>
            ))}
          </div>
          
          {/* Right side - smaller carousel with enhanced visuals */}
          <div className={cn(
            "lg:col-span-7 relative transition-all duration-1000 h-[400px]", // Sized to roughly match the 2 cards
            isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-20"
          )}>
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              className="w-full h-full [&>div]:h-full"
              setApi={(api) => {
                carouselApiRef.current = api;
              }}
            >
              <CarouselContent className="h-full">
                {coreValues.map((value, index) => (
                  <CarouselItem key={index} className="relative h-full">
                    <div className="relative w-full h-full overflow-hidden rounded-xl shadow-2xl transform transition-all duration-500 hover:scale-[1.02]">
                      {/* Enhanced image with overlay and effects */}
                      <img 
                        src={value.image}
                        alt={value.title}
                        className={cn(
                          "w-full h-full object-cover transition-all duration-700",
                          activeIndex === index ? "scale-100 filter-none" : "scale-110 blur-sm brightness-75"
                        )}
                      />
                      
                      {/* Cooler gradient overlay - more vibrant */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-80"></div>
                      
                      {/* Dynamic light effect */}
                      {activeIndex === index && (
                        <>
                          <div className="absolute top-0 right-0 w-[150%] h-[150%] bg-gradient-to-br from-primary/20 via-sky-400/10 to-transparent rounded-full blur-2xl transform -translate-x-1/2 -translate-y-1/2 animate-[pulse_8s_ease_infinite]"></div>
                          <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-primary/20 rounded-full blur-2xl"></div>
                        </>
                      )}
                      
                      {/* Enhanced decorative elements */}
                      {activeIndex === index && (
                        <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
                          {/* Top border gradient */}
                          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary/0 via-primary/70 to-primary/0 shadow-[0_0_15px_rgba(var(--primary)/0.7)]"></div>
                          
                          {/* Corner decorations - keeping this star */}
                          <div className="absolute top-4 right-4">
                            <Sparkles className="h-5 w-5 text-primary/90 animate-pulse" />
                          </div>
                        </div>
                      )}
                      
                      {/* Enhanced bottom content overlay with glassmorphism */}
                      <div className="absolute bottom-0 left-0 right-0 p-6 backdrop-blur-sm bg-black/20 border-t border-white/10">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="bg-primary/20 p-2 rounded-full backdrop-blur-sm">
                            {value.icon}
                          </div>
                          <h3 className="text-2xl font-bold text-white">{value.title}</h3>
                        </div>
                        <p className="text-white/90 max-w-xl">
                          {value.content}
                        </p>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>
        </div>

        {/* Offer CTA */}
        <div className={cn(
          "mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-1000 delay-300",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        )}>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary text-white font-medium shadow-lg hover:shadow-xl hover:bg-primary/90 transition-all"
          >
            Claim Your Free Prototype
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CoreValues;
